import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ScenarioDiagramContext } from '../../../state/Context';
import { DurationField, TextField } from '../../form';
import { Durations } from '../../../constants';
import { createDuration, parseDuration } from '../../../utils/dateUtil';
import { useUpdateScenarioUnitMutation } from '../../../network/mutations';
import { getCapacityString } from '../../../utils/dataUtil';

function UnitDialog() {
  const { t } = useTranslation();
  const { scenarioId } = useParams();
  const {
    state: {
      open,
      data: { unit },
    },
    setState,
  } = useContext(ScenarioDiagramContext);
  const {
    mutate,
    isPending: isMutationPending,
    isSuccess: isMutationSuccess,
  } = useUpdateScenarioUnitMutation();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      capacity_override: 0,
      stay_info_override_mean_los: 0,
      stay_info_override_mean_los_unit: Durations.DAYS,
      stay_info_override_std_los: 0,
      stay_info_override_std_los_unit: Durations.DAYS,
    },
  });
  const onClose = () => setState({ open: false, data: {} });
  const onSubmit = (data) => {
    mutate({
      scenarioId,
      unitId: unit.id,
      data: {
        kind: 'set',
        capacity_override: data.capacity_override,
        stay_info_override: {
          mean_los: createDuration(
            data.stay_info_override_mean_los,
            data.stay_info_override_mean_los_unit,
          ),
          std_los: createDuration(
            data.stay_info_override_std_los,
            data.stay_info_override_std_los_unit,
          ),
        },
      },
    });
  };

  useEffect(() => {
    // TODO: show mutation error
    if (!isMutationPending && isMutationSuccess) {
      onClose();
    }
  }, [isMutationPending, isMutationSuccess]);

  useEffect(() => {
    if (!open || !unit) {
      reset();
    } else if (open && unit) {
      const mean = parseDuration(unit.stay_info?.mean_los);
      const std = parseDuration(unit.stay_info?.std_los);
      reset({
        capacity_override: getCapacityString(unit.capacity) || 0,
        stay_info_override_mean_los: mean[0] || 0,
        stay_info_override_mean_los_unit: mean[1] || Durations.DAYS,
        stay_info_override_std_los: std[0],
        stay_info_override_std_los_unit: std[1] || Durations.DAYS,
      });
    }
  }, [open, unit]);

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle sx={{ color: 'primary.main' }}>
        {unit?.name}
        <Typography color="textSecondary">{unit?.subtitle}</Typography>
      </DialogTitle>
      <DialogContent>
        <form id="unit-form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Typography variant="overline" color="textSecondary">
              {t('pages.scenarios.configure.units.stayInfo')}
            </Typography>
            <DurationField
              label={t('pages.scenarios.configure.units.form.meanLOS')}
              tooltip={t('pages.scenarios.configure.units.form.meanLOSTooltip')}
              name="stay_info_override_mean_los"
              unitName="stay_info_override_mean_los_unit"
              control={control}
              rules={{ required: t('common.validation.required') }}
            />
            <DurationField
              label={t('pages.scenarios.configure.units.form.deviationLOS')}
              tooltip={t(
                'pages.scenarios.configure.units.form.deviationLOSTooltip',
              )}
              name="stay_info_override_std_los"
              unitName="stay_info_override_std_los_unit"
              control={control}
              rules={{ required: t('common.validation.required') }}
            />
            <Typography variant="overline" color="textSecondary">
              {t('pages.scenarios.configure.units.capacity')}
            </Typography>
            <TextField
              label={t('pages.scenarios.configure.units.form.capacity')}
              tooltip={t(
                'pages.scenarios.configure.units.form.capacityTooltip',
              )}
              name="capacity_override"
              control={control}
              rules={{ required: t('common.validation.required') }}
            />
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button disabled={isMutationPending} onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          type="submit"
          form="unit-form"
          startIcon={<FontAwesomeIcon icon="floppy-disk" />}
          loading={isMutationPending}
          loadingPosition="start"
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UnitDialog;
